import {getSubmissionHandlingFields} from "../utils/form-contact-fields";
export {getOrderedContactFields, getSubmissionHandlingFields} from "../utils/form-contact-fields";
import PropTypes from "prop-types";
import classNames from "classnames";
import {getSteps, orderFieldsById, validate} from "@autocx/forms/src/utils";
import CheckIcon from "@heroicons/react/solid/CheckIcon";
import {textAlignment} from "../utils/block-utils";
import AnimateHeight from "react-animate-height";
import Form from "@autocx/forms";
import {WebsiteContext} from "../index";
import {useContext, useEffect, useRef, useState} from "react";
import axios from "axios";
import _get from "lodash/get";
import _cloneDeep from "lodash/cloneDeep";
import _set from "lodash/set";
import _unset from "lodash/unset";
import {getActionLink} from "./action";
import WebsitePaymentField, {
    clearWebsitePaymentRecoveryForResponse,
    getPaymentCustomerAccountMode,
    getWebsitePaymentAccessTokenForResponse,
    sanitisePaymentFormValues
} from "./website-payment-field";
import useHCaptcha from "../utils/use-hcaptcha";
import useCap from "../utils/use-cap";
import {PROVIDER_CAP, PROVIDER_HCAPTCHA, resolveCaptchaProvider} from "../utils/captcha-provider";

export const HONEY_POT_FIELD_NAME = 'hp_rOjpajZy97BsNhvhRc3dJ';
const LEGACY_STEP_ID = 'legacyStep';
const WEBSITE_FORM_CUSTOM_CONTROLS = {payment: WebsitePaymentField};
const getWebsiteControlId = (formId, controlId) => formId ? `${formId}--${controlId}` : controlId;

function extractCaptchaToken(values, formElement) {
    const directKeys = [
        'hcaptchaToken',
        'hCaptchaToken',
        'cap-token',
        'capToken',
        'captchaToken',
        'h-captcha-response',
    ];

    for (const key of directKeys) {
        const value = values?.[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
    }

    const nestedPaths = [
        'security.hcaptchaToken',
        'customProperties.hcaptchaToken',
        'security.captchaToken',
        'customProperties.captchaToken',
        'security.capToken',
        'customProperties.capToken'
    ];

    for (const path of nestedPaths) {
        const value = _get(values, path);
        if (typeof value === 'string' && value.trim()) return value.trim();
    }

    if (formElement) {
        const fromDom =
            formElement.querySelector('input[name="cap-token"]')?.value
            || formElement.querySelector('input[name="capToken"]')?.value
            || formElement.querySelector('input[name="captchaToken"]')?.value
            || formElement.querySelector('input[name="h-captcha-response"]')?.value;

        if (typeof fromDom === 'string' && fromDom.trim()) return fromDom.trim();
    }

    return null;
}

export function prepareWebsiteFormSubmission(values, formElement, formRenderedAtUtc) {
    const honeyPotElement = formElement?.elements?.namedItem?.(HONEY_POT_FIELD_NAME)
        || formElement?.querySelector(`[name="${HONEY_POT_FIELD_NAME}"]`)
        || formElement?.querySelector(`#${HONEY_POT_FIELD_NAME}`);
    values[HONEY_POT_FIELD_NAME] = honeyPotElement?.value
        || honeyPotElement?._valueTracker?.getValue()
        || '';
    values.formRenderedAtUtc = formRenderedAtUtc;

    const hCaptchaToken = extractCaptchaToken(values, formElement);
    if (hCaptchaToken) values.hcaptchaToken = hCaptchaToken;

    // A browser-provided status is not evidence that hCaptcha succeeded. Only forward the token so each receiving
    // endpoint can verify it independently.
    [
        'hcaptchaStatus',
        'hCaptchaStatus',
        'captchaStatus',
        'hcaptchaValidation',
        'captchaValidation',
        'customProperties.hcaptchaStatus',
        'customProperties.hCaptchaStatus',
        'security.hcaptchaStatus',
        'security.hCaptchaStatus'
    ].forEach(path => _unset(values, path));

    return values;
}

WebsiteForm.propTypes = {
    form: PropTypes.object,
    onSubmit: PropTypes.func,
    onSubmitted: PropTypes.func,
    onValuesChange: PropTypes.func,
    onSubmittingChange: PropTypes.func,
    initialValues: PropTypes.object,
    onFieldChange: PropTypes.func,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    submitting: PropTypes.bool,
    submitted: PropTypes.bool,
};

WebsiteForm.defaultProps = {
    onSubmit: () => {
    },
    onSubmitted: () => {
    },
    onValuesChange: () => {
    },
    onSubmittingChange: () => {
    },
    onFieldChange: () => {
    },
};

const Steps = ({currentStep, steps, onStepClick}) => {
    return (
        <nav aria-label="Progress" className="w-full mb-4">
            <ol role="list" className="flex items-center">
                {steps?.map((step, stepIdx) => (
                        <li key={stepIdx}
                            className={classNames(stepIdx !== steps.length - 1 ? 'flex-grow' : '', 'relative')}>
                            {currentStep > (stepIdx + 1) ? (
                                <>
                                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                        <div className="h-0.5 w-full bg-base"/>
                                    </div>
                                    <div onClick={(e) => {
                                        e.preventDefault();
                                        if (onStepClick) onStepClick(stepIdx + 1);
                                    }}
                                         className="relative flex h-6 w-6 items-center justify-center rounded-full bg-base hover:bg-base cursor-pointer">
                                        <CheckIcon className="h-5 w-5 text-white" aria-hidden="true"/>
                                    </div>
                                </>
                            ) : currentStep === (stepIdx + 1) ? (
                                <>
                                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                        <div className="h-0.5 w-full bg-gray-200"/>
                                    </div>
                                    <div
                                        className="relative flex h-6 w-6 items-center justify-center rounded-full border-2 border-base bg-white"
                                        aria-current="step"
                                    >
                                        <span className="h-2.5 w-2.5 rounded-full bg-base" aria-hidden="true"/>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                        <div className="h-0.5 w-full bg-gray-200"/>
                                    </div>
                                    <div
                                        className="group relative flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                                    <span className="h-2.5 w-2.5 rounded-full bg-transparent"
                                          aria-hidden="true"/>
                                    </div>
                                </>
                            )}
                        </li>
                    )
                )}
            </ol>
        </nav>
    )
}

const flattenFields = (fields = []) => {
    const flat = [];

    fields.forEach(field => {
        if (Array.isArray(field.fields)) {
            flat.push(...flattenFields(field.fields));
        } else {
            flat.push(field);
        }
    });

    return flat;
};

export const placeOptionalPaymentPasswordAfterPayment = (fields = [], registrationSettings = {}) => {
    if (registrationSettings.customerAccountMode !== 'optional' ||
        registrationSettings.allowInlineRegistration !== true) return fields;

    const passwordIndex = fields.findIndex(field => field?.name === 'customer.password');
    const paymentIndex = fields.findIndex(field => flattenFields([field])
        .some(candidate => candidate?.type === 'payment'));
    if (passwordIndex < 0 || paymentIndex < 0 || passwordIndex === paymentIndex + 1) return fields;

    const ordered = [...fields];
    const [passwordField] = ordered.splice(passwordIndex, 1);
    const nextPaymentIndex = ordered.findIndex(field => flattenFields([field])
        .some(candidate => candidate?.type === 'payment'));
    ordered.splice(nextPaymentIndex + 1, 0, passwordField);
    return ordered;
};

export const formHasPaymentField = (fieldsets = []) => (
    fieldsets.some(fieldset => flattenFields(fieldset?.fields || [])
        .some(field => field?.type === 'payment'))
);

export const getPaymentRegistrationSettings = (fieldsets = []) => {
    const paymentFields = fieldsets.flatMap(fieldset => flattenFields(fieldset?.fields || []))
        .filter(field => field?.type === 'payment');

    const accountModes = paymentFields.map(getPaymentCustomerAccountMode);
    const customerAccountMode = accountModes.length > 0 && accountModes.every(mode => mode === accountModes[0])
        ? accountModes[0]
        // Multiple payment fields are rejected by the server. Keep malformed client data conservative instead of
        // accidentally weakening an account-required payment because another field allows guests.
        : accountModes.length > 0 ? 'required' : null;

    return {
        hasPaymentField: paymentFields.length > 0,
        customerAccountMode,
        allowGuestPayment: customerAccountMode === 'none',
        allowsGuestPayment: customerAccountMode === 'none' || customerAccountMode === 'optional',
        // The server permits one payment field per form. Treat a malformed multi-payment form conservatively: every
        // payment field must allow inline registration before the form may solicit new account credentials.
        allowInlineRegistration: paymentFields.length > 0 &&
            paymentFields.every(field => field.allowInlineRegistration !== false)
    };
};

export const shouldIncludeSystemStep = (includeSystemStep, registrationSettings) => (
    includeSystemStep && (
        !registrationSettings?.hasPaymentField ||
        registrationSettings.customerAccountMode === 'required'
    )
);

export const getEffectivePaymentSubmissionRules = (rules = {}, registrationSettings) => {
    if (!registrationSettings?.hasPaymentField) return rules;

    if (registrationSettings.customerAccountMode === 'none') {
        return {
            ...rules,
            registerCustomer: false,
            enableLogin: false
        };
    }

    const nextRules = {
        ...rules,
        customer: true,
        registerCustomer: registrationSettings.allowInlineRegistration,
        enableLogin: registrationSettings.allowInlineRegistration
    };

    if (!registrationSettings.allowInlineRegistration) return nextRules;
    if (Number(nextRules.matchBy) === 2) {
        nextRules.matchBy = 3;
        nextRules.requriedFields = 3;
    } else {
        nextRules.matchBy = [1, 3].includes(Number(nextRules.matchBy)) ? Number(nextRules.matchBy) : 1;
        if (nextRules.matchBy === 3 && Number(nextRules.requriedFields) === 2) nextRules.requriedFields = 3;
    }

    return nextRules;
};

const hasLegacyStepField = (fieldsets = []) => {
    const fields = fieldsets?.[0]?.fields || [];
    return fields.some(field => field?.id === LEGACY_STEP_ID);
};

const buildContactFieldOrder = (rules, includeLegacyStep) => {
    let order = Array.isArray(rules?.contactFieldOrder)
        ? rules.contactFieldOrder.filter(id => id !== 'customerStep' && id !== null && id !== undefined && id !== '')
        : [];
    if (includeLegacyStep && !order.includes(LEGACY_STEP_ID)) {
        order = [LEGACY_STEP_ID, ...order];
    }
    return order;
};

export const isWebsiteFormPreview = (websiteContext, preview = false) => preview === true ||
    websiteContext?.builder === true || websiteContext?.picker === true;

export default function WebsiteForm(props) {
    const [height, setHeight] = useState('auto');
    const websiteContext = useContext(WebsiteContext);
    const isBuilderPreview = isWebsiteFormPreview(websiteContext, props.preview);
    const contentDiv = useRef(null);
    const formRenderedAtUtcRef = useRef(new Date().toISOString());
    const persistedValuesRef = useRef(_cloneDeep(props.initialValues ?? props.values) || {});
    const action = props?.values?.overrideFormAction ? props?.values?.action : props?.values?.form?.action
    const href = action && action?.type ? getActionLink(action, websiteContext, '/') : null;
    const [state, setState] = useState(() => {
        // noinspection UnnecessaryLocalVariableJS
        /**
         * Here to give the idea i hit as to what the return type is
         * @type {{}}
         */
        const form = props.values?.form ? {...props.values.form} : {}
        const legacyStepEnabled = form?.enableStep === true || form?.step?.title || form?.step?.text;
        const legacyStepInForm = hasLegacyStepField(form?.userDefinedFieldsets);
        const includeSystemStep = !(legacyStepEnabled || legacyStepInForm);
        const paymentRegistration = getPaymentRegistrationSettings(form?.userDefinedFieldsets);
        const effectiveRules = getEffectivePaymentSubmissionRules(form?.rules, paymentRegistration);
        const useAuthenticatedPaymentCustomer = paymentRegistration.hasPaymentField &&
            paymentRegistration.customerAccountMode !== 'none';
        const fields = getSubmissionHandlingFields(
            effectiveRules,
            false,
            HONEY_POT_FIELD_NAME,
            websiteContext,
            form?.step,
            shouldIncludeSystemStep(includeSystemStep, paymentRegistration),
            websiteContext?.customerSession,
            useAuthenticatedPaymentCustomer,
            paymentRegistration.allowInlineRegistration,
            paymentRegistration.customerAccountMode
        );
        const fieldsets = fields?.length > 0 ? fields : [];
        let newUserDefinedFieldsets = [];
        if (props.values?.form?.userDefinedFieldsets && props.values?.form?.userDefinedFieldsets?.[0]?.fields) {
            newUserDefinedFieldsets = props.values.form.userDefinedFieldsets.map(fieldset => ({
                ...fieldset,
                fields: (fieldset.fields || []).filter(field => !field?.system)
            }))
            if (legacyStepEnabled && newUserDefinedFieldsets[0]) {
                const hasLegacyStep = newUserDefinedFieldsets[0].fields?.some(field => field?.id === LEGACY_STEP_ID);
                if (!hasLegacyStep) {
                    newUserDefinedFieldsets[0].fields = [{
                        id: LEGACY_STEP_ID,
                        type: "step",
                        title: form?.step?.title,
                        text: form?.step?.text,
                        __cx__: {legacyStep: true}
                    }, ...newUserDefinedFieldsets[0].fields];
                }
            }
            const mergedFields = [...fieldsets, ...newUserDefinedFieldsets[0].fields];
            const order = buildContactFieldOrder(form?.rules, legacyStepEnabled || legacyStepInForm);
            newUserDefinedFieldsets[0].fields = placeOptionalPaymentPasswordAfterPayment(
                orderFieldsById(mergedFields, order),
                paymentRegistration
            );
        } else {
            const order = buildContactFieldOrder(form?.rules, legacyStepEnabled || legacyStepInForm);
            const baseFields = legacyStepEnabled
                ? [{
                    id: LEGACY_STEP_ID,
                    type: "step",
                    title: form?.step?.title,
                    text: form?.step?.text,
                    __cx__: {legacyStep: true}
                }, ...fieldsets]
                : fieldsets;
            newUserDefinedFieldsets = [{
                fields: placeOptionalPaymentPasswordAfterPayment(
                    orderFieldsById(baseFields, order),
                    paymentRegistration
                )
            }]
        }

        if (newUserDefinedFieldsets[0]) {
            delete newUserDefinedFieldsets[0].originalFields;
            delete newUserDefinedFieldsets[0].steps;
            delete newUserDefinedFieldsets[0].currentStep;
            delete newUserDefinedFieldsets[0].totalSteps;
        }
        form.userDefinedFieldsets = getSteps([...newUserDefinedFieldsets])
        return {
            error: false,
            errorMessage: null,
            submitted: false,
            submitting: false,
            form,
        }
    })
    const [trigger, setTrigger] = useState(0);

    const formUuid = state?.form?.uuid || state?.form?._id;
    const honeypotEnabled = websiteContext?.honeypotEnabled !== false;
    const captchaProvider = resolveCaptchaProvider({context: websiteContext, rules: state?.form?.rules});
    const hCaptchaEnabled = captchaProvider === PROVIDER_HCAPTCHA;
    const capEnabled = captchaProvider === PROVIDER_CAP;
    const {containerRef: hCaptchaContainerRef, controllerRef: hCaptchaControllerRef} = useHCaptcha({
        formId: formUuid,
        siteKey: hCaptchaEnabled ? websiteContext?.hcaptchaSiteKey : '',
    });
    const {containerRef: capContainerRef, controllerRef: capControllerRef} = useCap({
        formId: formUuid,
        apiEndpoint: capEnabled ? websiteContext?.capApiEndpoint : '',
    });
    const currentStep = state?.form?.userDefinedFieldsets?.[0]?.currentStep;
    const totalSteps = state?.form?.userDefinedFieldsets?.[0]?.totalSteps;
    const steps = state?.form?.userDefinedFieldsets?.[0]?.steps;
    const fileUploadSettings = {
        cdnUrl: websiteContext?.cdnUrl,
        saveUrl: `/api/v2/attachments/${websiteContext?.website?.uuid}`,
        basePath: websiteContext?.website?.uuid,
        source: 'website'
    };

    useEffect(() => {
        if ((!websiteContext.builder && !websiteContext.picker) || !props.values?.form?.uuid) return;
        const form = state?.form ? {...state?.form} : {}
        const legacyStepEnabled = form?.enableStep === true || form?.step?.title || form?.step?.text;
        const legacyStepInForm = hasLegacyStepField(form?.userDefinedFieldsets);
        const includeSystemStep = !(legacyStepEnabled || legacyStepInForm);
        const paymentRegistration = getPaymentRegistrationSettings(form?.userDefinedFieldsets);
        const effectiveRules = getEffectivePaymentSubmissionRules(form?.rules, paymentRegistration);
        const useAuthenticatedPaymentCustomer = paymentRegistration.hasPaymentField &&
            paymentRegistration.customerAccountMode !== 'none';
        const fields = getSubmissionHandlingFields(
            effectiveRules,
            false,
            HONEY_POT_FIELD_NAME,
            websiteContext,
            form?.step,
            shouldIncludeSystemStep(includeSystemStep, paymentRegistration),
            websiteContext?.customerSession,
            useAuthenticatedPaymentCustomer,
            paymentRegistration.allowInlineRegistration,
            paymentRegistration.customerAccountMode
        );
        const fieldsets = fields?.length > 0 ? fields : [];
        let newUserDefinedFieldsets = [];
        if (state?.form?.userDefinedFieldsets) {
            newUserDefinedFieldsets = state.form.userDefinedFieldsets.map(fieldset => ({
                ...fieldset,
                fields: (fieldset.fields || []).filter(field => !field?.system)
            }))
            if (legacyStepEnabled && newUserDefinedFieldsets[0]) {
                const hasLegacyStep = newUserDefinedFieldsets[0].fields?.some(field => field?.id === LEGACY_STEP_ID);
                if (!hasLegacyStep) {
                    newUserDefinedFieldsets[0].fields = [{
                        id: LEGACY_STEP_ID,
                        type: "step",
                        title: form?.step?.title,
                        text: form?.step?.text,
                        __cx__: {legacyStep: true}
                    }, ...newUserDefinedFieldsets[0].fields];
                }
            }
            const mergedFields = [...fieldsets, ...newUserDefinedFieldsets[0].fields];
            const order = buildContactFieldOrder(form?.rules, legacyStepEnabled || legacyStepInForm);
            newUserDefinedFieldsets[0].fields = placeOptionalPaymentPasswordAfterPayment(
                orderFieldsById(mergedFields, order),
                paymentRegistration
            );
        } else {
            const order = buildContactFieldOrder(form?.rules, legacyStepEnabled || legacyStepInForm);
            const baseFields = legacyStepEnabled
                ? [{
                    id: LEGACY_STEP_ID,
                    type: "step",
                    title: form?.step?.title,
                    text: form?.step?.text,
                    __cx__: {legacyStep: true}
                }, ...fieldsets]
                : fieldsets;
            newUserDefinedFieldsets = [{
                fields: placeOptionalPaymentPasswordAfterPayment(
                    orderFieldsById(baseFields, order),
                    paymentRegistration
                )
            }]
        }
        if (newUserDefinedFieldsets[0]) {
            delete newUserDefinedFieldsets[0].originalFields;
            delete newUserDefinedFieldsets[0].steps;
            delete newUserDefinedFieldsets[0].currentStep;
            delete newUserDefinedFieldsets[0].totalSteps;
        }
        form.userDefinedFieldsets = getSteps([...newUserDefinedFieldsets])

        setState({
            error: false,
            errorMessage: null,
            submitted: false,
            submitting: false,
            form
        })
    }, [state?.form?.rules, state?.form?.step]);

    // Handle when the builder has changed the form to update the form for this block 
    useEffect(() => {
        // Ignore it when we are not in the builder/picker or there is no form uuid to lookup
        if ((!websiteContext.builder && !websiteContext.picker) || !props.values?.form?.uuid) return;

        const cancelToken = axios.CancelToken.source();
        axios
            .get(`/api/v2/channels/${websiteContext.channel}/forms/${props.values.form.uuid}`, {cancelToken: cancelToken.token})
            .then(({data: form}) => {
                setState(previous => ({...previous, form}));
            })
            .catch(err => {
                if (!axios.isCancel(err)) console.log(err);
            })
        return () => cancelToken.cancel();
    }, [props.values?.form?.uuid]);

    useEffect(() => {
        if (contentDiv.current) {
            const element = contentDiv.current
            const resizeObserver = new ResizeObserver(() => {
                setHeight(element.clientHeight)
            })

            resizeObserver.observe(element)

            return () => resizeObserver.disconnect()
        }
    }, [contentDiv?.current])

    useEffect(() => {
        setState((p) => ({
            ...p,
            error: props.error,
            errorMessage: props.errorMessage,
            submitted: props.submitted,
            submitting: props.submitting
        }))
    }, [props.error, props.errorMessage, props.submitted, props.submitting]);

    useEffect(() => {
        props?.onSubmittingChange?.(state.submitting);
    }, [props?.onSubmittingChange, state.submitting]);

    useEffect(() => {
        const getParams = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const params = Object.fromEntries(urlParams);
            if (params?.formId !== formUuid && params?.formId !== '') return;

            const paramValues = {};
            delete params.formId;

            const fieldsets = state?.form?.userDefinedFieldsets || [];
            const allFields = fieldsets.flatMap(fs => flattenFields(fs.originalFields || fs.fields || []));

            Object.entries(params).forEach(([key, value]) => {
                const matchedField = allFields.find(f => f.label?.trim() === key.trim());
                const fieldName = matchedField?.name || matchedField?.id || key; // fallback if no match
                _set(paramValues, fieldName, value);
            });

            persistedValuesRef.current = {
                ...persistedValuesRef.current,
                ...paramValues,
            };
            props?.onValuesChange?.(persistedValuesRef.current);

            setTrigger(prev => prev + 1);
        };

        getParams();
        window.addEventListener('popstate', getParams);
        return () => {
            window.removeEventListener('popstate', getParams);
        };
    }, []);

    const setStep = (nextStep) => {
        const newFieldSet = getSteps([...(state?.form?.userDefinedFieldsets || [])], nextStep);
        setState(previous => ({
            ...previous,
            error: false,
            errorMessage: null,
            submitted: false,
            submitting: false,
            form: {
                ...state?.form,
                userDefinedFieldsets: newFieldSet
            }
        }))
    };
    const prepareSubmission = async (e, values) => {
        prepareWebsiteFormSubmission(values, e.target, formRenderedAtUtcRef.current);
        const hCaptchaController = hCaptchaControllerRef.current;
        const capController = capControllerRef.current;
        values.formRenderedAtUtc = formRenderedAtUtcRef.current;

        values.captchaProvider = captchaProvider;

        if (hCaptchaEnabled && hCaptchaController && !extractCaptchaToken(values, e.target)) {
            const executedToken = await hCaptchaController.execute();
            if (executedToken) {
                values.captchaToken = executedToken;
                values.captchaStatus = hCaptchaController.getStatus() || 'valid';
            }
        }

        if (capEnabled && capController) {
            const capToken = extractCaptchaToken(values, e.target) || capController.getToken() || await capController.execute();
            if (capToken) {
                values.captchaToken = capToken;
                values.capToken = capToken;
                values.captchaStatus = capController.getStatus() || 'valid';
            } else {
                // Let the server apply monitor/balanced/strict policy. In monitor
                // mode an unavailable provider must not lose a real enquiry.
                values.captchaStatus = capController.getStatus() || 'unverifiable';
            }
        }

        const captchaToken = extractCaptchaToken(values, e.target);
        if (captchaToken) {
            values.captchaToken = captchaToken;
        }
        return values;
    };

    return (
        <>
            {steps && steps.length > 1 && !state.submitted && (
                <Steps currentStep={currentStep} totalSteps={totalSteps} steps={steps} onStepClick={setStep}/>
            )}
            {state.submitted && !action?.type ? (
                <div className={classNames("flex flex-col w-full gap-1", textAlignment(props))}>
                    <div>
                        <div className={"inline-block"}>
                            <svg viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg" className={"w-14"}>
                                <g stroke="currentColor" strokeWidth="2" fill="none" fillRule="evenodd"
                                   strokeLinecap="round" strokeLinejoin="round">
                                    <path className="form-submit-circle"
                                          d="M13 1C6.372583 1 1 6.372583 1 13s5.372583 12 12 12 12-5.372583 12-12S19.627417 1 13 1z"/>
                                    <path className="form-submit-tick" d="M6.5 14.5L10 18 l8.808621-8.308621"/>
                                </g>
                            </svg>
                        </div>
                    </div>
                    {props?.values?.form?.customSuccessMessage
                        ?
                        <div dangerouslySetInnerHTML={{__html: props?.values?.form?.customSuccessMessage}}
                             className={"rich-text"}/>
                        :
                        <p>Thank you, we've received your submission.</p>
                    }
                </div>
            ) : (
                <AnimateHeight
                    height={(steps && steps.length > 1 || !websiteContext?.picker) ? height : 'auto'}
                    contentRef={contentDiv}
                    animateOpacity={true}
                    className={classNames(
                        (steps && steps.length > 1 || !websiteContext?.picker) ? "!overflow-visible" : null,
                        "flex flex-col w-full px-1"
                    )}>
                    <Form
                        id={state.form.uuid || state.form._id}
                        fileUploadSettings={fileUploadSettings}
                        noValidate={false}
                        values={persistedValuesRef.current}
                        onFieldChange={(e, name, value, values) => {
                            if (state.error) setState((p) => ({...p, error: false, errorMessage: ''}))
                            persistedValuesRef.current = values
                            props?.onValuesChange?.(values);
                            props?.onFieldChange?.(e, name, value, values);
                        }}
                        userDefinedFieldsets={state.form.userDefinedFieldsets}
                        fieldsets={state.form.fieldsets}
                        hiddenFields={state.form.hiddenFields}
                        layout={"website"}
                        customControls={WEBSITE_FORM_CUSTOM_CONTROLS}
                        className={classNames("flex flex-col w-full")}
                        onPrepareSubmit={prepareSubmission}
                        onProcessingChange={(submitting) => {
                            setState(previous => ({
                                ...previous,
                                submitting,
                                error: submitting ? false : previous.error,
                                errorMessage: submitting ? null : previous.errorMessage
                            }));
                        }}
                        onSubmit={async (e, values) => {
                            if (isBuilderPreview) {
                                e.preventDefault?.();
                                return;
                            }

                            setState(previous => ({...previous, submitting: true, error: false, errorMessage: null}));
                            const hCaptchaController = hCaptchaControllerRef.current;
                            const capController = capControllerRef.current;

                            // If there is any sign of a value on the honey pot input then ignore submission and go to success state
                            if (honeypotEnabled && values[HONEY_POT_FIELD_NAME]) {
                                setState({
                                    error: false,
                                    errorMessage: null,
                                    submitting: false,
                                    submitted: true,
                                })
                                return;
                            }
                            if (values.customer) values.customer.enableLogin = state?.form?.rules?.enableLogin ?? true

                            if (!values.captchaProvider) await prepareSubmission(e, values);

                            values.form = {uuid: e.target.id};
                            if (props.node) values.node = props.node;
                            if (props.product) values.product = props.product;

                            // Remove all whitespace from the number if it's set when the phone number is present
                            const matchBy = state.form.rules?.matchBy;
                            if (matchBy === 2 || matchBy === 3) {
                                const number = _get(values, 'customer.contactNumber');
                                if (number) {
                                    _set(values, 'customer.contactNumber', number.replace(/ +/g, '').trim());
                                } else {
                                    _unset(values, 'customer.contactNumber')
                                }
                            }

                            const paymentOperationAccessToken = getWebsitePaymentAccessTokenForResponse(
                                websiteContext.channel,
                                values.form.uuid,
                                values
                            );
                            const safeValues = sanitisePaymentFormValues(values);
                            const requestValues = paymentOperationAccessToken
                                ? {...safeValues, paymentOperationAccessToken}
                                : safeValues;

                            return axios
                                .post(`/api/v2/channels/${websiteContext.channel}/responses`, requestValues)
                                .then(async response => {
                                    // Keep the successful operation available across refreshes until the server has
                                    // accepted the linked form response. Only then is it safe to forget the retry key.
                                    clearWebsitePaymentRecoveryForResponse(
                                        websiteContext.channel,
                                        values.form.uuid,
                                        values
                                    );
                                    hCaptchaController?.reset();
                                    capController?.reset();
                                    if (props?.values?.form?.formSubmission) {
                                        try {
                                            await Promise.resolve(new Function(props.values.form.formSubmission)())
                                        } catch (callbackError) {
                                            console.error(callbackError)
                                        }
                                    }

                                    try {
                                        await Promise.resolve(props?.onSubmitted?.(values, response?.data));
                                    } catch (callbackError) {
                                        console.error(callbackError)
                                    }
                                    setState(previous => ({...previous, submitted: true, submitting: false}))

                                    if (action && action?.type && href) {
                                        // Redirect users after delay
                                        setTimeout(() => {
                                            window.location.href = href;
                                        }, 2000)
                                    }

                                })
                                .catch(err => {
                                    hCaptchaController?.reset();
                                    capController?.reset();
                                    if (err?.response?.status === 409 && typeof err?.response?.data === 'string') setState(previous => ({
                                        ...previous,
                                        error: true,
                                        errorMessage: err.response.data,
                                        submitting: false
                                    }))
                                    else {
                                        // Do not log the Axios error object: payment acknowledgements may carry a
                                        // guest continuation credential in the request config.
                                        console.log('Website form submission failed', err?.response?.status ?? 'network-error')
                                        setState(previous => ({...previous, error: true, submitting: false}))
                                    }
                                });
                        }}>
                        {honeypotEnabled ? (
                        <p hidden>
                            <input
                                id={getWebsiteControlId(formUuid, HONEY_POT_FIELD_NAME)}
                                type="text"
                                name={HONEY_POT_FIELD_NAME}
                                autoComplete="off"
                            />
                        </p>
                        ) : null}

                        {state.error ? (
                            <div
                                role="alert"
                                className={"mt-3 rounded-theme border border-red-200 bg-red-50 px-4 py-3 text-red-700 font-semibold leading-tight text-sm animate__animated animate__headShake"}>
                                {state.errorMessage ?? "There was a problem sending the message, please try again."}
                            </div>
                        ) : null}
                        {hCaptchaEnabled ? (
                            <div className={"mt-4"}>
                                <div ref={hCaptchaContainerRef} data-hcaptcha-container="true"/>
                            </div>
                        ) : null}
                        {capEnabled ? (
                            <div hidden aria-hidden="true">
                                <div ref={capContainerRef} data-cap-container="true"/>
                            </div>
                        ) : null}
                        <div className={classNames(
                            currentStep > 1 && currentStep <= totalSteps ? 'flex justify-between space-x-6' : null
                        )}>
                            {currentStep > 1 && currentStep <= totalSteps && (
                                <div className={"flex justify-end mt-4"}>
                                    <button
                                        type="submit"
                                        disabled={state.submitting}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setStep(currentStep - 1);
                                        }}
                                        className={classNames("button hover:opacity-80 relative", state.submitting ? "pointer-events-none" : null)}>
                                        <span>Back</span>
                                    </button>
                                </div>
                            )}
                            {currentStep < totalSteps && (
                                <div className={"flex ml-auto justify-end mt-4"}>
                                    <button
                                        type="submit"
                                        form={state.form.uuid}
                                        disabled={state.submitting}
                                        onClick={(e) => {
                                            const form = document.getElementById(formUuid)
                                            const isValid = validate(form, setState, false);
                                            if (!isValid) return;
                                            e.preventDefault();
                                            setStep(currentStep + 1);
                                        }}
                                        className={classNames("button hover:opacity-80 relative", state.submitting ? "pointer-events-none" : null)}>
                                        <span>Next</span>
                                    </button>
                                </div>
                            )}
                            {currentStep === totalSteps ? (
                                <div className={"flex justify-end flex-grow mt-4"}>
                                    <button
                                        type={isBuilderPreview ? "button" : "submit"}
                                        disabled={state.submitting || state.submitted}
                                        aria-disabled={isBuilderPreview || undefined}
                                        aria-busy={state.submitting}
                                        className={classNames("button overflow-hidden justify-center hover:opacity-80 relative", props?.submitButtonClassName, (state.submitting || state.submitted) ? "pointer-events-none" : null)}
                                        form={state.form.uuid}>
                                            <span
                                                className={classNames("transition ease-in duration-200", (state.submitting || state.submitted) ? "opacity-0" : "opacity-100")}>{props?.label || "Submit"}</span>
                                        {state.submitting ? (
                                            <svg
                                                className={"absolute animate-spin h-5 w-5 top-0 bottom-0 left-0 right-0 m-auto"}
                                                xmlns="http://www.w3.org/2000/svg" fill="none"
                                                viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"/>
                                                <path className="opacity-75" fill="currentColor"
                                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                            </svg>
                                        ) : null}
                                        {state.submitted && action?.type &&
                                            <span
                                                className={classNames(
                                                    "absolute flex transition ease-in duration-200 animate__animated",
                                                    state.submitted ? "animate__fadeInUp" : "opacity-0"
                                                )}
                                            >
                                                <svg viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg"
                                                     className={"w-6"}>
                                                    <g stroke="currentColor" strokeWidth="2" fill="none"
                                                       fillRule="evenodd"
                                                       strokeLinecap="round" strokeLinejoin="round">
                                                        <path className="form-submit-circle"
                                                              d="M13 1C6.372583 1 1 6.372583 1 13s5.372583 12 12 12 12-5.372583 12-12S19.627417 1 13 1z"/>
                                                        <path className="form-submit-tick"
                                                              d="M6.5 14.5L10 18 l8.808621-8.308621"/>
                                                    </g>
                                                </svg>
                                            </span>
                                        }
                                    </button>
                                </div>
                            ) : null}
                        </div>
                        </Form>
                </AnimateHeight>
            )}
        </>
    )
};
